import { useState, useEffect, useCallback } from "react";
import { o2cApi, type O2COrder, type O2COrderStatus } from "../api/o2c";
import { useCompany } from "../context/CompanyContext";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { PageSkeleton } from "../components/PageSkeleton";
import { EmptyState } from "../components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ShoppingCart,
  Plus,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  ArrowRight,
} from "lucide-react";

const STATUS_CONFIG: Record<
  O2COrderStatus,
  { label: string; color: string; icon: typeof Clock }
> = {
  pending: { label: "Pending", color: "bg-yellow-500/10 text-yellow-600", icon: Clock },
  quoted: { label: "Quoted", color: "bg-blue-500/10 text-blue-600", icon: Clock },
  negotiating: { label: "Negotiating", color: "bg-purple-500/10 text-purple-600", icon: ArrowRight },
  accepted: { label: "Accepted", color: "bg-green-500/10 text-green-600", icon: CheckCircle2 },
  delivered: { label: "Delivered", color: "bg-green-500/10 text-green-600", icon: CheckCircle2 },
  disputed: { label: "Disputed", color: "bg-red-500/10 text-red-600", icon: AlertTriangle },
  completed: { label: "Completed", color: "bg-green-500/10 text-green-600", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", color: "bg-gray-500/10 text-gray-500", icon: XCircle },
  refunded: { label: "Refunded", color: "bg-orange-500/10 text-orange-600", icon: XCircle },
};

function OrderCard({ order, companyId }: { order: O2COrder; companyId: string }) {
  const [disputeReason, setDisputeReason] = useState("");
  const [showDispute, setShowDispute] = useState(false);
  const [busy, setBusy] = useState(false);
  const cfg = STATUS_CONFIG[order.status] ?? STATUS_CONFIG.pending;
  const StatusIcon = cfg.icon;
  const isBuyer = order.buyerCompanyId === companyId;
  const isSeller = order.sellerCompanyId === companyId;

  const handleStatusUpdate = async (status: O2COrderStatus) => {
    setBusy(true);
    try {
      await o2cApi.updateOrderStatus(order.id, status);
      window.location.reload();
    } catch (e) {
      console.error("Status update failed:", e);
    } finally {
      setBusy(false);
    }
  };

  const handleDispute = async () => {
    if (!disputeReason) return;
    setBusy(true);
    try {
      await o2cApi.disputeOrder(order.id, disputeReason, companyId);
      window.location.reload();
    } catch (e) {
      console.error("Dispute failed:", e);
    } finally {
      setBusy(false);
    }
  };

  const handleResolve = async (refundBuyer: boolean) => {
    setBusy(true);
    try {
      await o2cApi.resolveDispute(order.id, refundBuyer ? "Buyer refunded" : "Seller confirmed", refundBuyer);
      window.location.reload();
    } catch (e) {
      console.error("Resolve failed:", e);
    } finally {
      setBusy(false);
    }
  };

  const handleSettle = async () => {
    setBusy(true);
    try {
      await o2cApi.settleOrder(order.id);
      window.location.reload();
    } catch (e) {
      console.error("Settle failed:", e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div className="flex items-center gap-2">
          <StatusIcon className="h-4 w-4" />
          <CardTitle className="text-sm font-medium">{order.productName}</CardTitle>
        </div>
        <Badge className={cfg.color} variant="secondary">
          {cfg.label}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div>
            <span className="text-muted-foreground">Qty</span>
            <p className="font-medium">{order.quantity}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Unit</span>
            <p className="font-medium">${order.unitPriceUSD.toFixed(2)}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Total</span>
            <p className="font-medium">${order.totalUSD.toFixed(2)}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Fee (10%)</span>
            <p className="font-medium">${order.feeUSD.toFixed(2)}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Net</span>
            <p className="font-medium">${order.netUSD.toFixed(2)}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Currency</span>
            <p className="font-medium">{order.currency}</p>
          </div>
        </div>

        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <span>{isBuyer ? "Buying from" : "Selling to"}:</span>
          <span className="font-mono">
            {(isBuyer ? order.sellerCompanyId : order.buyerCompanyId).slice(0, 12)}...
          </span>
        </div>

        {/* O2C Flow Actions */}
        {order.status === "pending" && isSeller && (
          <div className="flex gap-2">
            <Button size="sm" onClick={() => handleStatusUpdate("quoted")} disabled={busy}>
              Send Quote
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleStatusUpdate("cancelled")} disabled={busy}>
              Reject
            </Button>
          </div>
        )}

        {order.status === "quoted" && isBuyer && (
          <div className="flex gap-2">
            <Button size="sm" onClick={() => handleStatusUpdate("accepted")} disabled={busy}>
              Accept & Commit
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleStatusUpdate("negotiating")} disabled={busy}>
              Counter-Offer
            </Button>
          </div>
        )}

        {order.status === "accepted" && isSeller && (
          <div className="flex gap-2">
            <Button size="sm" onClick={() => handleStatusUpdate("delivered")} disabled={busy}>
              Mark Delivered
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleStatusUpdate("completed")} disabled={busy}>
              Complete & Settle
            </Button>
          </div>
        )}

        {order.status === "delivered" && (
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSettle} disabled={busy}>
              Settle Payment
            </Button>
            <Button size="sm" variant="destructive" onClick={() => setShowDispute(!showDispute)} disabled={busy}>
              Dispute
            </Button>
          </div>
        )}

        {showDispute && (
          <div className="space-y-2">
            <Input
              placeholder="Dispute reason..."
              value={disputeReason}
              onChange={(e) => setDisputeReason(e.target.value)}
            />
            <Button size="sm" variant="destructive" onClick={handleDispute} disabled={busy || !disputeReason}>
              File Dispute
            </Button>
          </div>
        )}

        {order.status === "disputed" && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => handleResolve(true)} disabled={busy}>
              Refund Buyer
            </Button>
            <Button size="sm" onClick={() => handleResolve(false)} disabled={busy}>
              Confirm Seller
            </Button>
          </div>
        )}

        <div className="text-xs text-muted-foreground pt-1 border-t">
          {new Date(order.createdAt).toLocaleString()}
        </div>
      </CardContent>
    </Card>
  );
}

export default function CommercePage() {
  const { selectedCompanyId } = useCompany();
  const { setBreadcrumbs } = useBreadcrumbs();
  const [orders, setOrders] = useState<O2COrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newOrder, setNewOrder] = useState({
    sellerCompanyId: "",
    productName: "",
    quantity: "1",
    unitPriceUSD: "",
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    setBreadcrumbs([{ label: "Commerce" }]);
  }, [setBreadcrumbs]);

  const loadOrders = useCallback(async () => {
    if (!selectedCompanyId) return;
    setLoading(true);
    try {
      const result = await o2cApi.listOrders(selectedCompanyId);
      setOrders(Array.isArray(result) ? result : []);
    } catch (e) {
      console.error("Failed to load orders:", e);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleCreate = async () => {
    if (!selectedCompanyId || !newOrder.sellerCompanyId || !newOrder.productName) return;
    setCreating(true);
    try {
      await o2cApi.createOrder({
        buyerCompanyId: selectedCompanyId,
        sellerCompanyId: newOrder.sellerCompanyId,
        productName: newOrder.productName,
        quantity: parseInt(newOrder.quantity) || 1,
        unitPriceUSD: parseFloat(newOrder.unitPriceUSD) || 0,
      });
      setShowCreate(false);
      setNewOrder({ sellerCompanyId: "", productName: "", quantity: "1", unitPriceUSD: "" });
      loadOrders();
    } catch (e) {
      console.error("Create order failed:", e);
    } finally {
      setCreating(false);
    }
  };

  if (!selectedCompanyId) {
    return (
      <EmptyState
        icon={ShoppingCart}
        message="Select a company to view commerce orders."
      />
    );
  }

  if (loading) {
    return <PageSkeleton variant="list" />;
  }

  const activeOrders = orders.filter(
    (o) => !["completed", "cancelled", "refunded"].includes(o.status),
  );
  const pastOrders = orders.filter((o) =>
    ["completed", "cancelled", "refunded"].includes(o.status),
  );

  return (
    <div className="w-full max-w-7xl space-y-5">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-xl font-semibold text-foreground">
          Commerce <span className="text-muted-foreground">({orders.length})</span>
        </h1>
        <Button size="sm" onClick={() => setShowCreate(!showCreate)}>
          <Plus className="mr-1 h-4 w-4" />
          New Order
        </Button>
      </div>

      {showCreate && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Create O2C Order (INTENT)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              placeholder="Seller Company ID"
              value={newOrder.sellerCompanyId}
              onChange={(e) => setNewOrder({ ...newOrder, sellerCompanyId: e.target.value })}
            />
            <Input
              placeholder="Product name"
              value={newOrder.productName}
              onChange={(e) => setNewOrder({ ...newOrder, productName: e.target.value })}
            />
            <div className="flex gap-2">
              <Input
                type="number"
                placeholder="Quantity"
                value={newOrder.quantity}
                onChange={(e) => setNewOrder({ ...newOrder, quantity: e.target.value })}
                className="w-32"
              />
              <Input
                type="number"
                placeholder="Unit price (USD)"
                value={newOrder.unitPriceUSD}
                onChange={(e) => setNewOrder({ ...newOrder, unitPriceUSD: e.target.value })}
                className="w-40"
              />
            </div>
            <Button size="sm" onClick={handleCreate} disabled={creating || !newOrder.sellerCompanyId || !newOrder.productName}>
              {creating ? "Creating..." : "Send INTENT"}
            </Button>
          </CardContent>
        </Card>
      )}

      {activeOrders.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-medium text-muted-foreground">Active Orders</h2>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {activeOrders.map((order) => (
              <OrderCard key={order.id} order={order} companyId={selectedCompanyId} />
            ))}
          </div>
        </div>
      )}

      {pastOrders.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-medium text-muted-foreground">Past Orders</h2>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {pastOrders.map((order) => (
              <OrderCard key={order.id} order={order} companyId={selectedCompanyId} />
            ))}
          </div>
        </div>
      )}

      {orders.length === 0 && (
        <EmptyState
          icon={ShoppingCart}
          message="No commerce orders yet. Create one to start the O2C flow."
        />
      )}
    </div>
  );
}
